/**
 * Barrière de session — intercepteur UNIQUE et autonome (ADR 0016, spec 01 §5.4).
 *
 * TOUTES les requêtes HTTP (`raw()` d'api.ts) et connexions socket (fabrique
 * `createGameSocket`) passent par ce module : stop / reprise / gestion 401 /
 * sortie succès / sortie erreur vivent ICI, jamais dans les services appelants.
 *
 * Machine à états : `fresh → refreshing → fresh | dead`, avec une **génération
 * de session** (incrémentée à chaque refresh réussi). Transparence totale : pour
 * l'appelant, un appel = une promesse résolue avec l'issue FINALE — les 401
 * intermédiaires, l'attente du refresh et les rejeux sont invisibles. L'appelant
 * ne voit un 401 de session que si la session est MORTE (refresh KO).
 *
 * S'y ajoute la **fraîcheur proactive** (brique 3, ADR 0016 décision 4 —
 * confort, la barrière ci-dessus reste LE mécanisme de correction) : refresh
 * planifié vers ~80 % du TTL de l'access token quand l'onglet est visible,
 * rattrapage sur `visibilitychange`/`online`/`focus`.
 *
 * Aucune dépendance à React ni aux services : testable seul.
 */

const CSRF_COOKIE = 'rp_csrf';
const CSRF_HEADER = 'X-CSRF-Token';

export function getCookie(name: string): string | null {
  const escaped = name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  const value = match?.[1];
  return value !== undefined ? decodeURIComponent(value) : null;
}

export function csrfHeaders(): Record<string, string> {
  const token = getCookie(CSRF_COOKIE);
  return token ? { [CSRF_HEADER]: token } : {};
}

type ExpiredListener = () => void;

let generation = 0; // +1 à chaque refresh réussi (ou nouvelle session)
let dead = false; // session déclarée morte (refresh KO / anomalie)
let hadSession = false; // une session compte a réellement été observée
let refreshing: Promise<boolean> | null = null;
const expiredListeners = new Set<ExpiredListener>();

// ---- Fraîcheur proactive (brique 3, ADR 0016 décision 4) ----

interface SessionExpiry {
  expiresAt: number; // époque ms — fin de validité du JWT access
  ttlMs: number; // TTL total à l'émission (situe le seuil des 80 %)
}

/** Échéance PARTAGÉE entre onglets (localStorage) : un onglet qui rafraîchit recale les autres. */
const EXPIRY_STORAGE_KEY = 'rp:session-expiry';
/** Web Lock inter-onglets : deux POST /auth/refresh concurrents avec le même
 * cookie déclencheraient la détection de rejeu (famille révoquée, spec 01 §6). */
const REFRESH_LOCK_NAME = 'rp:session-refresh';
const PROACTIVE_RATIO = 0.8; // refresh vers 80 % du TTL
const PROACTIVE_JITTER_MS = 10_000; // anti-troupeau : deux onglets ne tirent pas ensemble
const LEARN_DELAY_MS = 5_000; // échéance inconnue → un refresh d'apprentissage rapide
const NETWORK_RETRY_MS = 60_000; // panne réseau du refresh proactif → nouvel essai borné
const MIN_DELAY_MS = 1_000;
/** L'API renvoie toujours `accessExpiresIn` (AuthSessionDto) ; filet si le corps
 * était illisible — un TTL trop court ne coûte qu'un refresh un peu tôt. */
const FALLBACK_TTL_S = 900;

let expiry: SessionExpiry | null = null;
let proactiveTimer: ReturnType<typeof setTimeout> | null = null;
let wakeListenersArmed = false;

function readSharedExpiry(): SessionExpiry | null {
  try {
    const raw = localStorage.getItem(EXPIRY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { expiresAt?: unknown; ttlMs?: unknown };
    if (typeof parsed.expiresAt !== 'number' || typeof parsed.ttlMs !== 'number') return null;
    return { expiresAt: parsed.expiresAt, ttlMs: parsed.ttlMs };
  } catch {
    return null; // stockage indisponible : dégradé par onglet
  }
}

function writeSharedExpiry(value: SessionExpiry | null): void {
  try {
    if (value) localStorage.setItem(EXPIRY_STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(EXPIRY_STORAGE_KEY);
  } catch {
    /* stockage indisponible : dégradé par onglet */
  }
}

/** Instant du refresh proactif : émission + 80 % du TTL. */
function refreshDueAt(e: SessionExpiry): number {
  return e.expiresAt - (1 - PROACTIVE_RATIO) * e.ttlMs;
}

/** Nouvelle échéance apprise (login/signup/refresh) → partagée + timer recalé. */
function noteExpiry(ttlSeconds: number): void {
  expiry = { expiresAt: Date.now() + ttlSeconds * 1000, ttlMs: ttlSeconds * 1000 };
  writeSharedExpiry(expiry);
  scheduleProactiveRefresh();
}

function stopProactiveRefresh(): void {
  if (proactiveTimer !== null) clearTimeout(proactiveTimer);
  proactiveTimer = null;
  expiry = null;
  writeSharedExpiry(null); // session finie : les autres onglets ne doivent pas s'y fier
}

function scheduleProactiveRefresh(): void {
  if (proactiveTimer !== null) clearTimeout(proactiveTimer);
  proactiveTimer = null;
  if (dead || !hadSession) return;
  armWakeListeners();
  const jitter = Math.random() * PROACTIVE_JITTER_MS;
  const delay = expiry
    ? Math.max(refreshDueAt(expiry) - Date.now(), 0) + jitter
    : LEARN_DELAY_MS + jitter; // échéance inconnue : on l'apprend d'un premier refresh
  proactiveTimer = setTimeout(onProactiveDue, Math.max(delay, MIN_DELAY_MS));
}

function onProactiveDue(): void {
  proactiveTimer = null;
  if (dead || !hadSession) return;
  // Onglet caché : timers throttlés/gelés par le navigateur de toute façon —
  // le rattrapage se fait au réveil (`onWake`), jamais en arrière-plan.
  if (document.visibilityState !== 'visible') return;
  void proactiveRefresh();
}

async function proactiveRefresh(): Promise<void> {
  // Un autre onglet a peut-être déjà rafraîchi (cookies partagés) : s'il reste
  // de la marge sur l'échéance PARTAGÉE, se recaler dessus sans poster.
  const shared = readSharedExpiry();
  if (shared && Date.now() < refreshDueAt(shared)) {
    expiry = shared;
    scheduleProactiveRefresh();
    return;
  }
  const refreshed = await refreshOnce(); // succès → noteExpiry a déjà reprogrammé
  if (!refreshed && !dead && proactiveTimer === null) {
    // Panne réseau (pas un refus) : nouvel essai borné — `online` rattrape aussi.
    proactiveTimer = setTimeout(onProactiveDue, NETWORK_RETRY_MS);
  }
}

/** Retour au premier plan / réseau retrouvé : recaler ou rattraper immédiatement. */
function onWake(): void {
  if (dead || !hadSession) return;
  if (document.visibilityState !== 'visible') return;
  const shared = readSharedExpiry();
  if (shared) expiry = shared; // un autre onglet a pu rafraîchir pendant la veille
  if (expiry && Date.now() < refreshDueAt(expiry)) {
    scheduleProactiveRefresh(); // encore frais : simple recalage du timer
    return;
  }
  if (proactiveTimer !== null) clearTimeout(proactiveTimer);
  proactiveTimer = null;
  void proactiveRefresh(); // seuil dépassé (ou échéance inconnue) : rattrapage
}

function armWakeListeners(): void {
  if (wakeListenersArmed) return;
  wakeListenersArmed = true;
  document.addEventListener('visibilitychange', onWake);
  window.addEventListener('focus', onWake);
  window.addEventListener('online', onWake);
}

function disarmWakeListeners(): void {
  if (!wakeListenersArmed) return;
  wakeListenersArmed = false;
  document.removeEventListener('visibilitychange', onWake);
  window.removeEventListener('focus', onWake);
  window.removeEventListener('online', onWake);
}

/** Sérialisation INTER-ONGLETS du refresh : le second onglet part avec le cookie
 * déjà tourné par le premier (simple rotation) au lieu de rejouer l'ancien. */
async function withCrossTabLock<T>(section: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return section(); // vieux navigateur : dégradé (jitter + pré-check restent)
  return locks.request(REFRESH_LOCK_NAME, section) as Promise<T>;
}

/** 401 de SESSION ? Exclut `invalid_credentials` (rejouer compterait double dans le lockout). */
async function isSession401(res: Response): Promise<boolean> {
  if (res.status !== 401) return false;
  try {
    const data = (await res.clone().json()) as { error?: unknown };
    return data.error !== 'invalid_credentials';
  } catch {
    return true;
  }
}

function declareDead(): void {
  dead = true;
  const wasLive = hadSession;
  hadSession = false;
  stopProactiveRefresh(); // le refresh a échoué : la session est morte pour TOUT le profil
  // Jamais d'événement pour un simple visiteur qui n'a jamais eu de session.
  if (wasLive) for (const listener of [...expiredListeners]) listener();
}

function doRefresh(): Promise<boolean> {
  return withCrossTabLock(async () => {
    let res: Response;
    try {
      res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: csrfHeaders(),
      });
    } catch {
      return false; // panne réseau : AUCUN verdict — la session n'est pas déclarée morte
    }
    if (res.ok) {
      generation += 1;
      dead = false;
      hadSession = true;
      // Échéance du nouveau token (brique 3) : `accessExpiresIn` de la réponse.
      let ttl = FALLBACK_TTL_S;
      try {
        const data = (await res.json()) as { accessExpiresIn?: unknown };
        if (typeof data.accessExpiresIn === 'number' && data.accessExpiresIn > 0) {
          ttl = data.accessExpiresIn;
        }
      } catch {
        /* corps illisible : filet — la barrière reste la correction */
      }
      noteExpiry(ttl);
      return true;
    }
    declareDead(); // refus définitif (refresh_invalid…) — le serveur a purgé les cookies
    return false;
  });
}

/** Refresh « single-flight » : un seul POST /auth/refresh à la fois, partagé HTTP + WS. */
function refreshOnce(): Promise<boolean> {
  refreshing ??= doRefresh().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

export const sessionGate = {
  /** Génération de session courante (avance à chaque refresh réussi). */
  get generation(): number {
    return generation;
  },

  get isDead(): boolean {
    return dead;
  },

  /** Attend un refresh en cours (les sockets l'attendent avant `connect()`). */
  async waitFresh(): Promise<void> {
    if (refreshing) await refreshing;
  },

  /**
   * Session compte observée (boot `me()` OK, login/signup) : arme `onExpired`
   * et la fraîcheur proactive. `accessExpiresIn` (secondes, AuthSessionDto) est
   * transmis quand une session vient d'être ÉMISE (login/signup/changement de
   * mdp) ; absent (boot `me()`), l'échéance est relue du stockage partagé —
   * sinon un refresh d'apprentissage l'apprendra.
   */
  noteAuthenticated(accessExpiresIn?: number): void {
    if (typeof accessExpiresIn === 'number' && accessExpiresIn > 0) {
      expiry = { expiresAt: Date.now() + accessExpiresIn * 1000, ttlMs: accessExpiresIn * 1000 };
      writeSharedExpiry(expiry);
    } else if (!expiry) {
      expiry = readSharedExpiry();
    }
    if (dead || !hadSession) {
      dead = false;
      hadSession = true;
      generation += 1;
    }
    scheduleProactiveRefresh();
  },

  /** Déconnexion volontaire : plus de session, SANS événement d'expiration. */
  noteLoggedOut(): void {
    dead = false;
    hadSession = false;
    generation += 1;
    stopProactiveRefresh();
  },

  /** La session vient de mourir en cours d'usage (refresh KO) → réaction par surface. */
  onExpired(listener: ExpiredListener): () => void {
    expiredListeners.add(listener);
    return () => {
      expiredListeners.delete(listener);
    };
  },

  /**
   * Sockets : `connect_error(session_stale)` → refresh partagé. `true` = la
   * session est (re)devenue fraîche, retenter la connexion ; `false` = morte.
   */
  async reportStaleConnection(generationAtFailure: number): Promise<boolean> {
    if (dead) return false;
    if (generation > generationAtFailure) return true; // déjà rafraîchie entre-temps
    return refreshOnce();
  },

  /**
   * Enveloppe UNE requête (transparence, spec 01 §5.4) : attend si un refresh
   * est en cours ; sur 401 de session, refresh single-flight puis rejeu — y
   * compris les 401 TARDIFS (requête partie avant un refresh, revenue après :
   * rejeu direct sans nouveau refresh). UN rejeu par génération : un 401 rejoué
   * dans la MÊME génération = anomalie → session morte, pas de boucle.
   */
  async run(send: () => Promise<Response>): Promise<Response> {
    await this.waitFresh();
    if (dead) return send(); // session morte : passage direct (les surfaces ont réagi)
    let sentGeneration = generation;
    let res = await send();
    let replayedGeneration = -1;
    while (!dead && (await isSession401(res))) {
      if (generation === sentGeneration) {
        if (replayedGeneration === generation) {
          declareDead(); // rejeu frais → encore 401 : anomalie, on ne boucle pas
          break;
        }
        if (!(await refreshOnce())) break; // morte (ou réseau KO) → le 401 remonte
      }
      // Génération avancée (par ce refresh, un appel concurrent ou un 401 tardif) → rejeu.
      sentGeneration = generation;
      replayedGeneration = generation;
      res = await send();
    }
    return res;
  },

  /** TESTS UNIQUEMENT : remise à zéro de l'état du module. */
  _resetForTests(): void {
    generation = 0;
    dead = false;
    hadSession = false;
    refreshing = null;
    expiredListeners.clear();
    stopProactiveRefresh();
    disarmWakeListeners();
  },
};
